import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { useBewerberKanban, useUpdateBewerber } from '../../api/bewerber';
import { byStatusEntryAsc, byStatusEntryDesc } from '../../utils/kanbanOrder';
import { useConfirm } from '../../context/ConfirmContext';
import {
  BewerberStatus,
  Bewerber,
  ONBOARDING_STEPS,
  offeneOnboardingSchritte,
  EINSTELLEN_PHASEN,
  EINSTELLEN_PHASEN_REIHENFOLGE,
  EinstellenPhase,
  einstellenPhase,
} from '../../types/bewerber';
import { gewonnenRueckfrage } from '../../utils/gewonnenRueckfrage';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCardOverlay } from './KanbanCard';
import { AbsageModal } from './AbsageModal';
import { NichtGeeignetModal } from './NichtGeeignetModal';
import { EinladenModal } from './EinladenModal';
import { useEinladenBewerber } from './actions/useEinladenBewerber';

const STORAGE_KEY = 'kanban-collapsed-bewerber';
const SORT_STORAGE_KEY = 'kanban-sort-reversed-bewerber';

/**
 * The intake columns are worked off as a queue, so there the order can be
 * flipped to oldest-first. Everywhere else the newest-first default stays.
 */
const SORT_REVERSIBLE = new Set<string>([
  BewerberStatus.NEU,
  BewerberStatus.KEINE_ANLAGEN,
  BewerberStatus.ZU_KLAEREN,
  BewerberStatus.NICHT_GEEIGNET,
]);

const STATUS_ORDER: BewerberStatus[] = [
  BewerberStatus.NEU,
  BewerberStatus.KEINE_ANLAGEN,
  BewerberStatus.ZU_KLAEREN,
  BewerberStatus.NICHT_GEEIGNET,
  BewerberStatus.GEEIGNET,
  BewerberStatus.EINGELADEN,
  BewerberStatus.VORSTELLUNG,
  BewerberStatus.IM_POOL,
  BewerberStatus.KENNENLERNGESPRAECH,
  BewerberStatus.ONBOARDING,
  BewerberStatus.GEWONNEN,
  BewerberStatus.OFFBOARDING,
  BewerberStatus.VERLOREN,
];

/**
 * A board column. Every status is one column, except ONBOARDING ("Bew.
 * einstellen"), which is split into its three waiting states. The phase is
 * derived from the data (einstellenPhase), the status stays ONBOARDING.
 */
interface BoardColumn {
  key: string;
  status: BewerberStatus;
  phase?: EinstellenPhase;
}

const einstellenKey = (phase: EinstellenPhase) => `${BewerberStatus.ONBOARDING}:${phase}`;

const COLUMNS: BoardColumn[] = STATUS_ORDER.flatMap((status): BoardColumn[] =>
  status === BewerberStatus.ONBOARDING
    ? EINSTELLEN_PHASEN_REIHENFOLGE.map((phase) => ({ key: einstellenKey(phase), status, phase }))
    : [{ key: status, status }],
);

const COLUMN_BY_KEY = new Map(COLUMNS.map((column) => [column.key, column]));

const columnKeyOf = (bewerber: Bewerber): string =>
  bewerber.status === BewerberStatus.ONBOARDING ? einstellenKey(einstellenPhase(bewerber)) : bewerber.status;

/** Why a card stands in the Einstellen column it does — for the info toasts. */
function phasenGrund(bewerber: Bewerber, phase: EinstellenPhase): string {
  if (phase === 'UNTERSCHRIFT') return 'die Unterlagen sind als gesendet abgehakt.';
  if (phase === 'AV_ERSTELLEN') return 'der Bedarf steht auf „Onboarding“, die Unterlagen sind noch nicht gesendet.';
  return bewerber.zugeordneteBedarfe?.length
    ? 'der zugeordnete Bedarf steht noch nicht auf „Onboarding“.'
    : 'es ist noch kein Bedarf zugeordnet.';
}

export interface KanbanBoardFilters {
  search: string;
  status: BewerberStatus | '';
  ortStelle: string;
  assignedUserId: string;
}

interface KanbanBoardProps {
  filters?: KanbanBoardFilters;
}

export function KanbanBoard({ filters }: KanbanBoardProps) {
  const { data, isLoading, error } = useBewerberKanban();
  const updateMutation = useUpdateBewerber();
  const einladen = useEinladenBewerber();
  const confirm = useConfirm();
  const [activeBewerber, setActiveBewerber] = useState<Bewerber | null>(null);
  const [absageModalBewerber, setAbsageModalBewerber] = useState<Bewerber | null>(null);
  const [nichtGeeignetModalBewerber, setNichtGeeignetModalBewerber] = useState<Bewerber | null>(null);

  // Collapsed columns state with localStorage persistence
  // Keyed by column, not status: the three Einstellen columns collapse on their own.
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Save to localStorage when collapsed state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...collapsedColumns]));
  }, [collapsedColumns]);

  const toggleColumnCollapse = useCallback((key: string) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Columns showing the oldest card first, remembered per browser like the collapse state.
  const [reversedColumns, setReversedColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(SORT_STORAGE_KEY);
      const keys: unknown = saved ? JSON.parse(saved) : [];
      return new Set(
        Array.isArray(keys) ? keys.filter((k): k is string => typeof k === 'string' && SORT_REVERSIBLE.has(k)) : [],
      );
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify([...reversedColumns]));
    } catch {
      // Blocked storage: the switch still works, it just isn't remembered.
    }
  }, [reversedColumns]);

  const toggleColumnSort = useCallback((key: string) => {
    setReversedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Custom collision detection that prioritizes columns
  const collisionDetection: CollisionDetection = useCallback((args) => {
    // First, check if pointer is within any droppable
    const pointerCollisions = pointerWithin(args);

    // If we have collisions, prefer column over cards
    if (pointerCollisions.length > 0) {
      // Check if any collision is a column
      const columnCollision = pointerCollisions.find(
        (collision) => COLUMN_BY_KEY.has(collision.id as string)
      );
      if (columnCollision) {
        return [columnCollision];
      }
      return pointerCollisions;
    }

    // Fall back to rect intersection for better column detection
    return rectIntersection(args);
  }, []);

  // Filter and group bewerbers by status
  const { bewerbersByColumn, totalVerlorenCount, filteredData } = useMemo(() => {
    const grouped: Record<string, Bewerber[]> = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));

    let items = data?.data || [];

    // Apply search filter
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      items = items.filter(
        (b) =>
          b.firstName?.toLowerCase().includes(searchLower) ||
          b.lastName?.toLowerCase().includes(searchLower) ||
          b.email?.toLowerCase().includes(searchLower) ||
          b.phone?.includes(searchLower) ||
          b.city?.name?.toLowerCase().includes(searchLower) ||
          (b.ortStellen ?? []).some((s) => s.name.toLowerCase().includes(searchLower))
      );
    }

    // Apply ortStelle filter (by stadt ID)
    if (filters?.ortStelle) {
      items = items.filter((b) => (b.ortStellen ?? []).some((s) => s.id === filters.ortStelle));
    }

    // Apply assigned-user filter
    if (filters?.assignedUserId) {
      items = items.filter((b) => b.assignedToId === filters.assignedUserId);
    }

    // Apply status filter (only show matching status column contents)
    // Note: We still show all columns for drag-drop, but only the filtered status will have items
    if (filters?.status) {
      items = items.filter((b) => b.status === filters.status);
    }

    // Group by column (status, and for ONBOARDING its phase)
    for (const bewerber of items) {
      grouped[columnKeyOf(bewerber)]?.push(bewerber);
    }

    // Every column: last moved in at the top. Editing or marking a card
    // does not move it — only a status change does. Within the Einstellen
    // columns that is the entry into ONBOARDING. The intake columns can be
    // flipped to oldest-first (SORT_REVERSIBLE).
    for (const key of Object.keys(grouped)) {
      grouped[key].sort(reversedColumns.has(key) ? byStatusEntryAsc : byStatusEntryDesc);
    }

    // For VERLOREN: limit to 10 items (already sorted above)
    const allVerloren = grouped[BewerberStatus.VERLOREN];
    const totalVerloren = allVerloren.length;
    grouped[BewerberStatus.VERLOREN] = allVerloren.slice(0, 10);

    return { bewerbersByColumn: grouped, totalVerlorenCount: totalVerloren, filteredData: items };
  }, [data?.data, filters?.search, filters?.ortStelle, filters?.status, filters?.assignedUserId, reversedColumns]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    // Find in filtered data first, then original data
    const bewerber = filteredData.find((b) => b.id === active.id) || data?.data.find((b) => b.id === active.id);
    if (bewerber) {
      // Block drag from VERLOREN - cannot reactivate via drag & drop
      if (bewerber.status === BewerberStatus.VERLOREN) {
        toast.info('VERLOREN Bewerber können nicht per Drag & Drop verschoben werden. Bitte die Aktion "Reaktivieren" verwenden.');
        return;
      }
      setActiveBewerber(bewerber);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveBewerber(null);

    if (!over) return;

    const bewerberId = active.id as string;
    // Find in filtered data first, then original data
    const bewerber = filteredData.find((b) => b.id === bewerberId) || data?.data.find((b) => b.id === bewerberId);

    if (!bewerber) return;

    // Determine the target column from the drop zone
    let target: BoardColumn | undefined = COLUMN_BY_KEY.get(over.id as string);
    if (!target) {
      // Dropped on another card - find its column
      const overBewerber = filteredData.find((b) => b.id === over.id) || data?.data.find((b) => b.id === over.id);
      if (overBewerber) {
        target = COLUMN_BY_KEY.get(columnKeyOf(overBewerber));
      }
    }

    if (!target || target.key === columnKeyOf(bewerber)) return;

    // Between the three Einstellen columns the status stays ONBOARDING.
    if (bewerber.status === BewerberStatus.ONBOARDING && target.status === BewerberStatus.ONBOARDING) {
      moveWithinEinstellen(bewerber, einstellenPhase(bewerber), target.phase!);
      return;
    }

    const newStatus = target.status;

    // Only update if status changed
    if (newStatus !== bewerber.status) {
      // Intercept VERLOREN drops - show AbsageModal instead of direct update.
      // Not from OFFBOARDING: there the employment simply ended, nobody was
      // rejected, so an Absagegrund would be the wrong question to ask.
      if (
        newStatus === BewerberStatus.VERLOREN &&
        bewerber.status !== BewerberStatus.OFFBOARDING
      ) {
        setAbsageModalBewerber(bewerber);
        return; // Don't update - wait for modal confirmation
      }

      // Intercept NICHT_GEEIGNET drops - show NichtGeeignetModal instead of direct update
      if (newStatus === BewerberStatus.NICHT_GEEIGNET) {
        setNichtGeeignetModalBewerber(bewerber);
        return; // Don't update - wait for modal confirmation
      }

      // Intercept EINGELADEN drops - the shared invite hook sends the
      // invitation email first and only changes status if the email succeeds.
      // For repeat invitations it shows the EinladenModal confirmation first.
      if (newStatus === BewerberStatus.EINGELADEN) {
        einladen.invite(bewerber);
        return; // Don't update - the hook handles the status change
      }

      // Offene Einstellen-Schritte sperren GEWONNEN nicht mehr, sie fragen nach.
      // Die Karte bleibt in ihrer Spalte, bis die Antwort ja ist.
      if (newStatus === BewerberStatus.GEWONNEN) {
        const rueckfrage = gewonnenRueckfrage(
          offeneOnboardingSchritte(bewerber),
          ONBOARDING_STEPS.length,
          'bewerber',
        );
        if (rueckfrage && !(await confirm(rueckfrage))) return;
      }

      updateMutation.mutate(
        {
          id: bewerberId,
          data: { status: newStatus },
        },
        {
          onSuccess: () => {
            // The Einstellen column follows from the data, not from where the
            // card was dropped — say so when the two differ.
            if (newStatus !== BewerberStatus.ONBOARDING || !target.phase) return;
            const landed = einstellenPhase(bewerber);
            if (landed !== target.phase) {
              toast.info(`Steht unter „${EINSTELLEN_PHASEN[landed].label}“ – ${phasenGrund(bewerber, landed)}`);
            }
          },
          onError: showUpdateError,
        }
      );
    }
  };

  const showUpdateError = (err: any) => {
    const responseMessage = err?.response?.data?.message;
    let errorMessage = 'Fehler beim Ändern des Status';
    if (Array.isArray(responseMessage)) {
      errorMessage = responseMessage.join(', ');
    } else if (typeof responseMessage === 'string') {
      errorMessage = responseMessage;
    }
    toast.error(errorMessage);
  };

  /**
   * A drag between the Einstellen columns. Only the step between "AV erstellen"
   * and "Warten auf Unterschrift" is a handgrip of ours — the "Unterlagen per
   * Mail gesendet" tick. "Warten auf KT-Go" follows from the Bedarf's status,
   * which is decided on the Bedarf, so a drag in or out of it changes nothing.
   */
  const moveWithinEinstellen = (bewerber: Bewerber, from: EinstellenPhase, to: EinstellenPhase) => {
    if (from === 'KT_GO' || to === 'KT_GO') {
      const bedarf = bewerber.zugeordneteBedarfe?.[0];
      if (from === 'KT_GO' && !bedarf) {
        toast.info('Erst einen Bedarf zuordnen – die Karte rückt weiter, sobald er auf „Onboarding“ steht.');
      } else if (from === 'KT_GO') {
        toast.info(
          `Die Karte rückt weiter, sobald der Bedarf „${bedarf!.childFirstName} ${bedarf!.childLastName ?? ''}“`.trimEnd() +
            ' auf „Onboarding“ steht (Kostenträger-Go).',
        );
      } else {
        toast.info('„Warten auf KT-Go“ ergibt sich aus dem Status des Bedarfs – dafür den Bedarf vor „Onboarding“ zurückziehen.');
      }
      return;
    }

    const gesendet = to === 'UNTERSCHRIFT';
    updateMutation.mutate(
      { id: bewerber.id, data: { onboardingUnterlagenGesendet: gesendet } },
      {
        onSuccess: () => {
          const landed = einstellenPhase({ ...bewerber, onboardingUnterlagenGesendet: gesendet });
          if (landed !== to) {
            toast.info(`Steht unter „${EINSTELLEN_PHASEN[landed].label}“ – ${phasenGrund(bewerber, landed)}`);
          } else if (gesendet) {
            toast.success('„Unterlagen per Mail gesendet“ abgehakt');
          }
        },
        onError: showUpdateError,
      },
    );
  };

  const handleDragCancel = () => {
    setActiveBewerber(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Fehler beim Laden der Bewerber: {error.message}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 lg:h-full">
        {COLUMNS.map((column) => (
          <KanbanColumn
            key={column.key}
            columnKey={column.key}
            status={column.status}
            label={column.phase && EINSTELLEN_PHASEN[column.phase].label}
            title={column.phase && EINSTELLEN_PHASEN[column.phase].beschreibung}
            bewerbers={bewerbersByColumn[column.key]}
            isCollapsed={collapsedColumns.has(column.key)}
            onToggleCollapse={() => toggleColumnCollapse(column.key)}
            totalCount={column.status === BewerberStatus.VERLOREN ? totalVerlorenCount : undefined}
            sortReversed={reversedColumns.has(column.key)}
            onToggleSort={SORT_REVERSIBLE.has(column.key) ? () => toggleColumnSort(column.key) : undefined}
          />
        ))}
      </div>

      <DragOverlay>
        {activeBewerber && <KanbanCardOverlay bewerber={activeBewerber} />}
      </DragOverlay>

      {/* Absage Modal for VERLOREN drops */}
      {absageModalBewerber && (
        <AbsageModal
          isOpen={true}
          onClose={() => setAbsageModalBewerber(null)}
          bewerber={absageModalBewerber}
        />
      )}

      {/* Nicht Geeignet Modal for NICHT_GEEIGNET drops */}
      {nichtGeeignetModalBewerber && (
        <NichtGeeignetModal
          isOpen={true}
          onClose={() => setNichtGeeignetModalBewerber(null)}
          bewerber={nichtGeeignetModalBewerber}
        />
      )}

      {/* Einladen Modal for repeat invitation confirmation on EINGELADEN drops */}
      <EinladenModal {...einladen.modalProps} />
    </DndContext>
  );
}
